/*
 * Purpose: Root app shell — routing, theme, global providers — P6 overlay wiring
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { DesignStyleProvider } from "@/components/design-style-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import Help from "./pages/Help";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import ComposePage from "./pages/Compose";
import LinkedInOAuthCallback from "./pages/LinkedInOAuthCallback";
import AuthSignIn from "./pages/AuthSignIn";
import AuthOAuthComplete from "./pages/AuthOAuthComplete";
import AuthLostKey from "./pages/AuthLostKey";
import FlowsLayout from "./pages/FlowsLayout";
import MyFlowsPage from "./pages/MyFlowsPage";
import FlowTemplatesPage from "./pages/FlowTemplatesPage";
import MissionPage from "./pages/MissionPage";
import ConnectPage from "./pages/ConnectPage";
import BuilderLegacyRedirect from "./pages/BuilderLegacyRedirect";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { AuthRouteGuard } from "@/components/auth/AuthRouteGuard";
import { AuthGateOverlay } from "@/components/auth/AuthGateOverlay";
import { AuthedHomeRedirect } from "@/components/auth/AuthedHomeRedirect";
import { AppShell } from "@/components/shell/AppShell";
import { APP_ROUTES } from "@/lib/appRoutes";
import PricingPage from "./pages/PricingPage";
import SystemHealthPage from "./pages/SystemHealthPage";
import AdminPage from "./pages/AdminPage";
import { AdminRouteGuard } from "@/components/auth/AdminRouteGuard";
import { CookieConsentBanner } from "@/components/analytics/CookieConsentBanner";
import { AnalyticsTracker } from "@/components/analytics/AnalyticsTracker";
import { SeoHead } from "@/components/seo/SeoHead";
import { JsonLdScript } from "@/components/seo/JsonLdScript";

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="workflow-ui-theme">
        <DesignStyleProvider>
        <TooltipProvider>
          <div data-ui-motion className="min-h-screen">
          <Toaster />
          <BrowserRouter>
            <AuthProvider>
            <SeoHead />
            <JsonLdScript />
            <AnalyticsTracker />
            <CookieConsentBanner />
            <Routes>
              <Route
                path={APP_ROUTES.home}
                element={
                  <AuthedHomeRedirect>
                    <Index />
                  </AuthedHomeRedirect>
                }
              />
              <Route path="/auth/sign-in" element={<AuthSignIn />} />
              <Route path="/auth/oauth/complete" element={<AuthOAuthComplete />} />
              <Route path="/auth/lost-key" element={<AuthLostKey />} />
              <Route path="/oauth/linkedin/callback" element={<LinkedInOAuthCallback />} />
              <Route path="/builder" element={<BuilderLegacyRedirect />} />
              <Route path="/setup" element={<Navigate to={APP_ROUTES.connectSetup} replace />} />
              <Route path={APP_ROUTES.systemStatus} element={<SystemHealthPage />} />
              <Route path={APP_ROUTES.pricing} element={<PricingPage />} />

              <Route element={<AuthRouteGuard />}>
                <Route element={<AuthGateOverlay />}>
                  <Route element={<AppShell />}>
                    <Route path={APP_ROUTES.dashboard} element={<Dashboard />} />
                    <Route path={APP_ROUTES.compose} element={<ComposePage />} />
                    <Route path={APP_ROUTES.flows} element={<FlowsLayout />}>
                      <Route index element={<MyFlowsPage />} />
                      <Route path="templates" element={<FlowTemplatesPage />} />
                    </Route>
                    <Route path="/mission/*" element={<MissionPage />} />
                    <Route path="/connect/*" element={<ConnectPage />} />
                    <Route path={APP_ROUTES.help} element={<Help />} />
                  </Route>
                </Route>
                <Route element={<AdminRouteGuard />}>
                  <Route element={<AppShell />}>
                    <Route path="/admin/*" element={<AdminPage />} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            </AuthProvider>
          </BrowserRouter>
          </div>
        </TooltipProvider>
        </DesignStyleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
